import { useState, useEffect, lazy, Suspense } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/lib/i18n/LanguageContext";

const SignupDialog = lazy(() => import("@/components/SignupDialog"));

const navItemsData = {
  nl: [
    { label: "Stad in balans", href: "/feiten" },
    { label: "Amsterdam in cijfers", href: "/cijfers" },
    { label: "Stemmen uit de stad", href: "/stemmen" },
    { label: "Verkiezingen", href: "/verkiezingen" },
    { label: "Ons advies", href: "/advies" },
    { label: "Nieuws", href: "/media" },
  ],
  en: [
    { label: "City in balance", href: "/feiten" },
    { label: "Amsterdam in numbers", href: "/cijfers" },
    { label: "Voices from the city", href: "/stemmen" },
    { label: "Elections", href: "/verkiezingen" },
    { label: "Our advice", href: "/advies" },
    { label: "News", href: "/media" },
  ],
};

const SiteHeader = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [signupOpen, setSignupOpen] = useState(false);
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const { lang, setLang, t } = useLanguage();
  const navItems = navItemsData[lang];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <>
      <header
        className={cn(
          "sticky top-0 z-50 border-b bg-background/95 backdrop-blur transition-all duration-300 supports-[backdrop-filter]:bg-background/80",
          scrolled && "shadow-sm"
        )}
      >
        <div className="container flex h-16 items-center justify-between md:h-[72px]">
          <Link to="/" className="group flex items-center gap-2">
            <img src="/images/logo-amsterdam-balans.svg" alt="Amsterdam in Balans logo" className="h-8 w-8 md:h-9 md:w-9" />
            <span className="relative font-heading text-xl font-black tracking-tight md:text-2xl">
              Amsterdam in <span className="text-primary">Balans</span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden items-center gap-0.5 lg:flex">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  "story-link rounded-md px-3 py-2 font-body text-[13px] font-medium tracking-wide transition-colors",
                  location.pathname === item.href
                    ? "font-bold text-primary"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setLang(lang === "nl" ? "en" : "nl")}
              className="flex items-center gap-1 rounded-md px-2 py-1.5 text-xs font-semibold text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label={lang === "nl" ? "Switch to English" : "Schakel naar Nederlands"}
            >
              <Globe className="h-3.5 w-3.5" />
              {lang === "nl" ? "EN" : "NL"}
            </button>

            <Button size="sm" className="hidden sm:inline-flex flex-col leading-tight py-2 px-4 h-auto" onClick={() => setSignupOpen(true)}>
              <span>{t("Kies voor een", "Choose for a")}</span>
              <span className="font-black">{t("Stad in balans", "City in balance")}</span>
            </Button>

            <button
              className="rounded-md p-2 transition-colors hover:bg-muted lg:hidden"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label={t("Menu openen", "Open menu")}
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile nav */}
        <div
          className={cn(
            "overflow-hidden border-t bg-background transition-all duration-300 lg:hidden",
            mobileOpen ? "max-h-[500px] opacity-100" : "max-h-0 border-t-0 opacity-0"
          )}
        >
          <nav className="container flex flex-col gap-1 py-4">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  "rounded-md px-3 py-2.5 font-body text-sm transition-colors",
                  location.pathname === item.href
                    ? "bg-primary/10 font-semibold text-primary"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
              >
                {item.label}
              </Link>
            ))}
            <Button size="sm" className="mt-3 sm:hidden flex-col leading-tight py-2 px-4 h-auto" onClick={() => setSignupOpen(true)}>
              <span>{t("Kies voor een", "Choose for a")}</span>
              <span className="font-black">{t("Stad in balans", "City in balance")}</span>
            </Button>
          </nav>
        </div>
      </header>

      <Suspense fallback={null}>
        <SignupDialog open={signupOpen} onOpenChange={setSignupOpen} />
      </Suspense>
    </>
  );
};

export default SiteHeader;
